import { Component, ErrorHandler, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'

import { ALKU_AIKOJEN_ALUSTA, TiimienHallintaService } from '../tiimien-hallinta.service'

export interface LisaaTiimikokonaisuusDialogData {
  // Avaimet jotka ovat jo käytössä — estetään päällekkäisyydet samassa dokumentissa
  varatutAvaimet: string[]
}

interface LisaaTiimikokonaisuusLomake {
  avain: string
  nimi: string
  alku: Date | null
}

@Component({
  templateUrl: './lisaa-tiimikokonaisuus.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LisaaTiimikokonaisuusDialog {
  lomake: FieldTree<LisaaTiimikokonaisuusLomake>

  virhe: string = null

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LisaaTiimikokonaisuusDialogData,
    private _dialogRef: MatDialogRef<LisaaTiimikokonaisuusDialog>,
    private _ladataanService: LadataanService,
    private _hallintaService: TiimienHallintaService,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
  ) {
    this.lomake = form(signal<LisaaTiimikokonaisuusLomake>({ avain: '', nimi: '', alku: null }), polku => {
      required(polku.avain)
      validoiReaktiivisesti(polku.avain, this._validateAvain)
      required(polku.nimi)
    })
  }

  peruuta() {
    this._dialogRef.close()
  }

  async tallenna() {
    if (!this.lomake().valid()) {
      return
    }
    this.virhe = null
    const arvot = this.lomake().value()
    const avain = (arvot.avain || '').trim().toUpperCase()
    const nimi = (arvot.nimi || '').trim()
    if (!avain || !nimi) {
      this.virhe = 'Avain ja nimi ovat pakollisia.'
      return
    }
    if (this.data.varatutAvaimet.includes(avain)) {
      this.virhe = 'Avain on jo käytössä.'
      return
    }
    const alku: NumberDate = arvot.alku ? this._dateService.dateToNumber(arvot.alku) : ALKU_AIKOJEN_ALUSTA
    try {
      this._ladataanService.aloitaLataaminen()
      await this._hallintaService.luoTiimikokonaisuus(avain, { nimi, alku })
      this._snackbar.open('Tiimikokonaisuus luotu', 'OK', { duration: 5000 })
      this._dialogRef.close({ avain })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Luominen epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  private _validateAvain: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    const arvo = ctrl.value
    if (arvo == null || arvo === '') {
      return null
    }
    if (typeof arvo !== 'string') {
      return { 'avain-virheellinen': true }
    }
    if (!/^[A-Z][A-Z0-9_]*$/.test(arvo.trim().toUpperCase())) {
      return { 'avain-virheellinen': true }
    }
    return null
  }
}
